import { Injectable } from '@angular/core';
import { CookieConsentService } from './cookie-consent.service';

/**
 * Injects vendor scripts once the matching consent grant exists, and only
 * then. Previously these two methods loaded unconditionally as soon as a host
 * app called them; the security review requires nothing optional to run
 * before a valid choice is recorded, so the gate now lives here rather than
 * being each caller's responsibility.
 *
 * Re-checks whenever consent changes, so a grant made after bootstrap (the
 * consent modal, or account settings) takes effect immediately without a
 * reload. Nothing here un-loads a script — withdrawing a tracker that already
 * ran still relies on the page reload the consent modal performs.
 */
@Injectable({
    providedIn: 'root'
})
export class TrackingScriptsService {
    private loadedSite24x7Rum = false;
    private loadedChurnZero = false;
    private site24x7AppKey: string | null = null;
    private churnZeroSrc: string | null = null;
    private subscribed = false;

    constructor(private readonly cookieConsentService: CookieConsentService) {}

    /**
     * Registers the Site24x7 app key and loads it now if already permitted.
     * Safe to call on every bootstrap; the key only needs to be supplied once.
     */
    loadSite24x7WhenPermitted(appKey: string): void {
        this.site24x7AppKey = appKey;
        this.ensureSubscribed();
        this.tryLoadPermitted();
    }

    /**
     * Registers the ChurnZero script URL and loads it now if already
     * permitted. Only the customer app calls this — admin has no ChurnZero.
     */
    loadChurnZeroWhenPermitted(src: string): void {
        this.churnZeroSrc = src;
        this.ensureSubscribed();
        this.tryLoadPermitted();
    }

    private ensureSubscribed(): void {
        if (this.subscribed) return;
        this.subscribed = true;
        this.cookieConsentService.consent$.subscribe(() => this.tryLoadPermitted());
    }

    private tryLoadPermitted(): void {
        if (this.site24x7AppKey && this.cookieConsentService.isAllowed('site24x7')) {
            this.injectSite24x7Rum(this.site24x7AppKey);
        }
        if (this.churnZeroSrc && this.cookieConsentService.isAllowed('churnzero')) {
            this.injectChurnZero(this.churnZeroSrc);
        }
    }

    /** Safe to call multiple times; only injects once. */
    private injectSite24x7Rum(appKey: string): void {
        const w = window as any;
        if (this.loadedSite24x7Rum || !w.performance || !w.performance.timing || !w.performance.navigation) {
            return;
        }
        this.loadedSite24x7Rum = true;

        w.s247r =
            w.s247r ||
            function (...args: unknown[]) {
                (w.s247r.q = w.s247r.q || []).push(args);
            };

        const script = document.createElement('script');
        script.async = true;
        script.setAttribute('data-tracker', 'site24x7');
        script.src = `//static.site24x7rum.in/beacon/site24x7rum-min.js?appKey=${appKey}`;
        document.head.appendChild(script);

        const existingOnError = window.onerror;
        window.onerror = function (message, source, lineno, colno, error) {
            if (existingOnError) {
                (existingOnError as OnErrorEventHandler).call(window, message, source, lineno, colno, error);
            }
            const err = error || new Error(String(message));
            (w.s247r.q = w.s247r.q || []).push(['captureException', err]);
            return false;
        };
    }

    /** Safe to call multiple times; only injects once. */
    private injectChurnZero(src: string): void {
        if (this.loadedChurnZero) {
            return;
        }
        this.loadedChurnZero = true;

        const w = window as any;
        w.ChurnZero = w.ChurnZero || [];

        const cz = document.createElement('script');
        cz.type = 'text/javascript';
        cz.async = true;
        cz.setAttribute('data-tracker', 'churnzero');
        cz.src = src;
        const existingScript = document.getElementsByTagName('script')[0];
        existingScript.parentNode.insertBefore(cz, existingScript);
    }
}
